import {
	AccessorKeyColumnDef,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	RowData,
	SortingState,
	useReactTable,
	Table as __Table,
} from '@tanstack/react-table';
import { CompanyDataEntry } from './CompaniesOverview';
import { useTranslation } from 'react-i18next';
import useTableState from '../useTableState';
import React, { useEffect } from 'react';
import { Button, FormControl, InputGroup, Table as BTable } from 'react-bootstrap';
import TableFooter from '../Components/TableFooter';
import TableBody from '../Components/TableBody';
import TableHeader from '../Components/TableHeader';

const tableName = 'company-table';

type Props = {
	data: CompanyDataEntry[];
	columns: AccessorKeyColumnDef<CompanyDataEntry, unknown>[];
	handleRowClick: (row: RowData) => void;
};

const Table = ({ data, columns, handleRowClick }: Props) => {
	const { t } = useTranslation();
	const { setPageIndex, setPageSize, pageIndex, pageSize, filters, setFilters } =
		useTableState(tableName);

	const [pagination, setPagination] = React.useState<PaginationState>({
		pageIndex,
		pageSize,
	});

	const [sorting, setSorting] = React.useState<SortingState>([
		{
			id: 'name',
			desc: false,
		},
	]);

	const [globalFilter, setGlobalFilter] = React.useState<string | null>(null);

	useEffect(() => {
		setPageIndex(pagination.pageIndex);
		setPageSize(pagination.pageSize);
	}, [pagination]);

	useEffect(() => {
		const storedGlobalFilter = filters.find((filter) => filter.id === 'global');
		if (storedGlobalFilter && !globalFilter) {
			setGlobalFilter(storedGlobalFilter.value as string);
		}
	}, [filters]);

	useEffect(() => {
		if (globalFilter !== null) {
			setFilters([
				{
					id: 'global',
					value: globalFilter,
				},
			]);
		}
	}, [globalFilter]);

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		enableGlobalFilter: true,
		onGlobalFilterChange: setGlobalFilter,
		state: {
			pagination,
			sorting,
			globalFilter,
		},
	});

	return (
		<>
			<div className="d-flex justify-content-end">
				<div className="d-flex flex-row align-items-center">
					<label className="me-2">{t('table.search')}: </label>
					<InputGroup size="sm">
						<FormControl
							value={globalFilter || ''}
							onChange={(e) => {
								setGlobalFilter(e.target.value);
							}}
						/>
						<Button variant="outline-secondary" onClick={() => setGlobalFilter('')}>
							<span className="glyphicons glyphicons-times" />
						</Button>
					</InputGroup>
				</div>
			</div>
			<BTable responsive hover size={'l'}>
				<TableHeader table={table as __Table<unknown>} />
				<TableBody table={table as __Table<unknown>} handleRowClick={handleRowClick} />
			</BTable>
			<TableFooter table={table as __Table<unknown>} />
		</>
	);
};

export default Table;
